import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { DataService } from 'src/app/data.service';

/**
 *  CSOPORT-IMPORT — a „Csoportok minden adata" lista beolvasása.
 *
 *  A tagság-import szándékosan nem hoz létre csoportot, és a tagsági listában csak az
 *  a csoport szerepel, amibe már jár valaki — az üres (jellemzően fakultációs) csoport
 *  onnan sosem keletkezne meg. Ez a törzsfájl viszont mindet tartalmazza, és két
 *  olyat is tud, amit a tagsági lista nem: az OSZTÁLYBONTÁST (ebből lesz az
 *  osztálykötés) és a CSOPORTTÍPUST (ebből, hogy számít-e a hiányzás).
 *
 *  A KÉPERNYŐ HÁROM MŰVELETET KÍNÁL, és az elemzés egyiket sem hajtja végre magától:
 *
 *    ÁTNEVEZÉS — ha csak a nevek romlottak el, ez a helyes út. A meglévő csoportot
 *      hozzárendeled a fájl sorához, és mi csak átnevezzük: az azonosító marad, tehát
 *      az ÓRAREND és a tantárgyfelosztás a helyén marad. Törlés + újralétrehozás után
 *      az egész órarendet újra fel kellene vinni.
 *    TÖRLÉS — ami az átnevezések után is fölösleges maradt. Külön döntés, alapból nincs.
 *    LÉTREHOZÁS — a fájl azon sorai, amiket átnevezés nem fedett le.
 */
@Component({
  selector: 'app-admin-importcsoport',
  templateUrl: './importcsoport.component.html',
  styleUrls: ['./importcsoport.component.scss'],
})
export class AdminImportCsoportComponent implements OnInit {
  intezmeny = '';
  fajlNev = '';
  fejlecek: string[] = [];
  nyersSorok: any[] = [];

  /** Oszlop-hozzárendelés: melyik fejléc mit jelent. */
  oszlop = { nev: '', osztaly: '', tipus: '', tanev: '' };
  /** Felülírható tanév (üresen a fájlból jön). */
  evKezi: number | null = null;

  elemzes: any = null;
  elemzesFut = false;

  /*  ── CSOPORT–TANTÁRGY KÖTÉS a tantárgyfelosztásból ────────────────────────
   *  Külön fájl, külön elemzés: ez nem törzsadatot hoz létre, hanem KÖTÉST köt.
   *  A csoport tantárgya több helyen kell (órarend, értékelés), de egyesével
   *  felvinni reménytelen – a KRÉTA tantárgyfelosztás-exportja viszont pont ezt
   *  tartalmazza soronként. */
  ttfFajlNev = '';
  ttfFejlecek: string[] = [];
  ttfNyers: any[] = [];
  ttfOszlop = { cel: '', tantargy: '' };
  ttfElemzes: any = null;
  ttfElemzesFut = false;
  ttfFut = false;
  ttfCsakFajl = false;
  ttfEredmeny: any = null;

  /*  FÁJL NÉLKÜL: a rendszer saját adatából (tantárgyfelosztás, meglévő órák). A
   *  csoportok jellemzően rég bent vannak, mire kiderül, hogy a kötésük hiányzik –
   *  ilyenkor nem kell fájlt keresni hozzá. */
  sajatElemzes: any = null;
  sajatFut = false;
  sajatEredmeny: any = null;
  fut = false;
  eredmeny: any = null;

  /** Meglévő csoport azonosítója → a fájlsor kulcsa, amire át kell nevezni. */
  parositas: Record<string, string> = {};
  torles: 'nincs' | 'nem_szereplo' | 'mind' = 'nincs';
  kenyszer = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  /* ── Fájl ────────────────────────────────────────────────────────────────
   *  A fejléceket felismerjük, de a hozzárendelés látszik és átállítható: egy
   *  ismeretlen fejlécű export se legyen zsákutca. */
  private static readonly NEV_NEVEK = ['csoport neve', 'csoportnév', 'csoport név', 'csoport', 'név'];
  private static readonly OSZTALY_NEVEK = ['osztálybontás', 'osztalybontas', 'osztály', 'osztaly'];
  private static readonly TIPUS_NEVEK = ['csoporttípus', 'csoporttipus', 'típus', 'tipus'];
  private static readonly TANEV_NEVEK = ['tanév', 'tanev'];

  fajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.fajlNev = file.name;
    this.elemzes = null;
    this.eredmeny = null;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const lap = wb.Sheets[wb.SheetNames[0]];
        const sorok: any[] = XLSX.utils.sheet_to_json(lap, { raw: false, defval: null });
        this.nyersSorok = sorok;
        this.fejlecek = sorok.length ? Object.keys(sorok[0]) : [];
        this.oszlopokFelismer();
        if (!sorok.length) this.data.sendToast('A fájl üres.', 'warning');
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni. Excel (.xlsx/.xls) vagy CSV kell.', 'error');
        this.nyersSorok = []; this.fejlecek = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  private oszlopokFelismer() {
    const talal = (jeloltek: string[]) => this.fejlecek.find((f) =>
      jeloltek.includes(String(f).trim().toLowerCase()))
      || this.fejlecek.find((f) => jeloltek.some((j) => String(f).trim().toLowerCase().includes(j)))
      || '';
    const C = AdminImportCsoportComponent;
    this.oszlop = {
      nev: talal(C.NEV_NEVEK),
      osztaly: talal(C.OSZTALY_NEVEK),
      tipus: talal(C.TIPUS_NEVEK),
      tanev: talal(C.TANEV_NEVEK),
    };
  }

  get kuldhetoSorok(): any[] {
    if (!this.oszlop.nev) return [];
    const ertek = (r: any, o: string) => (o ? String(r[o] ?? '').trim() : '');
    return (this.nyersSorok || [])
      .map((r) => ({
        nev: ertek(r, this.oszlop.nev),
        osztaly: ertek(r, this.oszlop.osztaly),
        tipus: ertek(r, this.oszlop.tipus),
        tanev: ertek(r, this.oszlop.tanev),
      }))
      .filter((r) => r.nev);
  }

  /* ── Elemzés ─────────────────────────────────────────────────────────── */

  async elemez() {
    const sorok = this.kuldhetoSorok;
    if (!sorok.length) {
      this.data.sendToast('Nincs feldolgozható sor — állítsd be a csoportnév oszlopát.', 'warning');
      return;
    }
    this.elemzesFut = true;
    this.eredmeny = null;
    try {
      this.elemzes = await firstValueFrom(this.http.post(
        `/api/import-csoport/elemzes/${this.intezmenyPath}`,
        { sorok, ev: this.evKezi || undefined }));
      /*  Párosítást NEM állítunk be magunktól. Egy rossz helyre került átnevezés
       *  csendesebb hiba, mint egy kimaradt sor: a javaslat ott van, de az ember
       *  fogadja el — egy gombbal mindet, vagy soronként. */
      this.parositas = {};
      this.torles = 'nincs';
      this.kenyszer = false;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
      this.elemzes = null;
    } finally {
      this.elemzesFut = false;
    }
  }

  /* ── Párosítás ───────────────────────────────────────────────────────── */

  /** A fájl azon sorai, amikre még nem neveztünk át semmit (plusz a sajátja). */
  szabadSorok(sajatKulcs: string): any[] {
    const foglalt = new Set(Object.entries(this.parositas)
      .filter(([, k]) => k && k !== sajatKulcs).map(([, k]) => k));
    return (this.elemzes?.sorok || [])
      .filter((r: any) => r.allapot === 'uj' && !foglalt.has(r.kulcs));
  }

  javaslatokatElfogad(kuszob = 0.5) {
    let db = 0;
    const foglalt = new Set<string>();
    for (const c of this.elemzes?.meglevok || []) {
      const j = c.javaslat;
      if (!j || j.pont < kuszob || foglalt.has(j.kulcs)) continue;
      this.parositas[c.id] = j.kulcs;
      foglalt.add(j.kulcs);
      db += 1;
    }
    this.data.sendToast(db ? `${db} párosítás elfogadva.` : 'Nincs elfogadható javaslat.',
      db ? 'success' : 'warning');
  }

  parositastTorol() { this.parositas = {}; }

  get parositasDb(): number {
    return Object.values(this.parositas).filter(Boolean).length;
  }

  /** Hány csoportot érintene a választott törlés — és mennyi közülük „akadályos". */
  get torlendo(): { db: number; akadalyos: number } {
    const o = this.elemzes?.osszegzes;
    if (!o || this.torles === 'nincs') return { db: 0, akadalyos: 0 };
    const parositott = new Set(Object.keys(this.parositas).filter((k) => this.parositas[k]));
    const kor = (this.elemzes.meglevok || []).filter((c: any) =>
      (this.torles === 'mind' || !c.fajlban) && !parositott.has(String(c.id)));
    return { db: kor.length, akadalyos: kor.filter((c: any) => c.ora || c.ttf).length };
  }

  szazalek(p: number): number { return Math.round((p || 0) * 100); }

  /* ── Végrehajtás ─────────────────────────────────────────────────────── */

  async vegrehajt() {
    const t = this.torlendo;
    const uzenet = [
      `Tanév: ${this.elemzes?.ev}.`,
      '',
      `Átnevezés: ${this.parositasDb} csoport (az azonosító marad, az órarend nem sérül).`,
      `Törlés: ${t.db} csoport${t.akadalyos ? ` — ebből ${t.akadalyos} órát vagy tantárgyfelosztást is érint` : ''}.`,
      `Létrehozás: legfeljebb ${(this.elemzes?.osszegzes?.uj || 0) - this.parositasDb} új csoport.`,
      ...(t.akadalyos && this.kenyszer
        ? ['', 'FIGYELEM: a kényszerítés be van kapcsolva. A törölt csoportok órái',
           'megmaradnak, de LEVÁLNAK a csoportról, a tantárgyfelosztás soraik pedig törlődnek.']
        : []),
      '',
      'Folytatod?',
    ].join('\n');
    if (!confirm(uzenet)) return;

    this.fut = true;
    try {
      this.eredmeny = await firstValueFrom(this.http.post(
        `/api/import-csoport/vegrehajt/${this.intezmenyPath}`,
        {
          sorok: this.kuldhetoSorok,
          ev: this.elemzes?.ev,
          parositas: this.parositas,
          torles: this.torles,
          kenyszer: this.kenyszer,
        }));
      this.data.sendToast('Az import lefutott.', 'success');
      // Újraelemzés: a képernyő a MOSTANI állapotot mutassa, ne a végrehajtás előttit.
      await this.elemez();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az import nem futott le.', 'error');
    } finally {
      this.fut = false;
    }
  }

  /* ── Csoport–tantárgy kötés ──────────────────────────────────────────────── */
  private static readonly TTF_CEL_NEVEK = ['osztály / csoport', 'osztaly / csoport', 'osztály/csoport', 'csoport', 'osztály'];
  private static readonly TTF_TARGY_NEVEK = ['tantárgy', 'tantargy', 'tárgy'];

  ttfFajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.ttfFajlNev = file.name;
    this.ttfElemzes = null;
    this.ttfEredmeny = null;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const sorok: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { raw: false, defval: null });
        this.ttfNyers = sorok;
        this.ttfFejlecek = sorok.length ? Object.keys(sorok[0]) : [];
        const talal = (jeloltek: string[]) => this.ttfFejlecek.find((f) =>
          jeloltek.includes(String(f).trim().toLowerCase()))
          || this.ttfFejlecek.find((f) => jeloltek.some((j) => String(f).trim().toLowerCase().includes(j)))
          || '';
        const C = AdminImportCsoportComponent as any;
        this.ttfOszlop = { cel: talal(C.TTF_CEL_NEVEK), tantargy: talal(C.TTF_TARGY_NEVEK) };
        if (!sorok.length) this.data.sendToast('A fájl üres.', 'warning');
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni.', 'error');
        this.ttfNyers = []; this.ttfFejlecek = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  /*  MELYIK TANÉVBE KÖTÜNK? A csoport és a tantárgy is tanévhez tartozik, ezért ezt
   *  nem tippelhetjük el. Sorrend: a felül megadott tanév → a fájl „Tanév" oszlopa
   *  („2026/2027" → 2026) → a mai naptól számított tanév. A képernyő kiírja, melyik lett. */
  get ttfEv(): number {
    if (this.evKezi) return Number(this.evKezi);
    const oszlop = (this.ttfFejlecek || []).find((f) =>
      ['tanév', 'tanev'].includes(String(f).trim().toLowerCase()));
    const m = oszlop ? String(this.ttfNyers?.[0]?.[oszlop] ?? '').match(/(\d{4})/) : null;
    if (m) return Number(m[1]);
    const most = new Date();
    return most.getMonth() + 1 >= 8 ? most.getFullYear() : most.getFullYear() - 1;
  }

  get ttfKuldheto(): any[] {
    if (!this.ttfOszlop.cel || !this.ttfOszlop.tantargy) return [];
    return (this.ttfNyers || [])
      .map((r) => ({
        cel: String(r[this.ttfOszlop.cel] ?? '').trim(),
        tantargy: String(r[this.ttfOszlop.tantargy] ?? '').trim(),
      }))
      .filter((r) => r.cel && r.tantargy);
  }

  async ttfElemez() {
    const sorok = this.ttfKuldheto;
    if (!sorok.length) { this.data.sendToast('Válaszd ki mindkét oszlopot.', 'warning'); return; }
    this.ttfElemzesFut = true;
    this.ttfEredmeny = null;
    try {
      this.ttfElemzes = await firstValueFrom(this.http.post(
        `/api/import-csoport/tantargy-elemzes/${this.intezmenyPath}`, { ev: this.ttfEv, sorok }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
    } finally {
      this.ttfElemzesFut = false;
    }
  }

  async ttfVegrehajt() {
    if (!this.ttfElemzes) return;
    if (!confirm(`${this.ttfElemzes.uj_kotes} új csoport–tantárgy kötés jön létre.\n\n`
      + (this.ttfCsakFajl
        ? 'A FÁJL A MÉRVADÓ: az érintett csoportok fájlban nem szereplő kötéseit töröljük.'
        : 'Meglévő kötést nem törlünk, csak hozzáadunk.')
      + '\n\nCsoportot, osztályt és tantárgyat nem hozunk létre.\n\nFolytatod?')) return;

    this.ttfFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/import-csoport/tantargy-vegrehajt/${this.intezmenyPath}`,
        { ev: this.ttfEv, sorok: this.ttfKuldheto, csak_fajl: this.ttfCsakFajl }));
      this.ttfEredmeny = r.eredmeny;
      this.ttfElemzes = r.allapot;
      this.data.sendToast(`${r.eredmeny.uj} új kötés, ${r.eredmeny.mar_megvolt} már megvolt`
        + (r.eredmeny.torolt ? `, ${r.eredmeny.torolt} törölve` : '') + '.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az import nem sikerült.', 'error');
    } finally {
      this.ttfFut = false;
    }
  }

  /* ── Kötés a rendszer saját adatából (fájl nélkül) ───────────────────────── */
  async sajatElemez() {
    this.sajatFut = true;
    this.sajatEredmeny = null;
    try {
      this.sajatElemzes = await firstValueFrom(this.http.post(
        `/api/import-csoport/tantargy-sajat-elemzes/${this.intezmenyPath}`, { ev: this.ttfEv }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
    } finally {
      this.sajatFut = false;
    }
  }

  async sajatVegrehajt() {
    if (!this.sajatElemzes?.parok?.length) return;
    if (!confirm(`${this.sajatElemzes.parok.length} csoport kap tantárgyat a rendszer saját adatából.\n\n`
      + 'Csak ott kötünk, ahol MÉG NINCS kötés, és csak ha egyértelmű — a több tantárgyú '
      + 'csoportokhoz nem nyúlunk.\n\nFolytatod?')) return;
    this.sajatFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/import-csoport/tantargy-sajat-vegrehajt/${this.intezmenyPath}`, { ev: this.ttfEv }));
      this.sajatEredmeny = r.eredmeny;
      this.sajatElemzes = r.allapot;
      this.data.sendToast(`${r.eredmeny.uj} kötés létrejött.`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kötés nem sikerült.', 'error');
    } finally {
      this.sajatFut = false;
    }
  }
}